<template>
  <div id="event-analytics" class="space-y-4 px-4 py-3">
    <section class="rounded-sm  bg-[#232b33] p-4 roundedn-sm">
      <div class="flex flex-wrap items-start justify-between gap-2">
        <div class="flex flex-wrap items-center gap-2">
          <el-date-picker
            v-model="filters.startDate"
            type="date"
            size="mini"
            style="max-width: 132px;"
            placeholder="Start date"
          ></el-date-picker>
          <el-date-picker
            v-model="filters.endDate"
            type="date"
            size="mini"
            style="max-width: 132px;"
            placeholder="End date"
          ></el-date-picker>
          <button
            class="h-7 px-3 text-xs font-bold tracking-tight rounded cursor-pointer transition-colors bg-yellow-600 text-black! hover:opacity-80"
            :disabled="loadingEvents"
            @click="loadEvents"
          >
            <i v-if="loadingEvents" class="el-icon-loading mr-1"></i>
            Load events
          </button>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <label class="inline-flex items-center gap-1.5 px-1 text-xs text-white/70 cursor-pointer">
            <span>All accounts</span>
            <input
              v-model="showAllEvents"
              type="checkbox"
              class="h-4 w-4 accent-emerald-500"
            >
          </label>

          <label class="inline-flex items-center gap-1.5 px-1 text-xs text-white/70 cursor-pointer">
            <span>AI summary</span>
            <input
              v-model="aiConfig.aiSummary"
              type="checkbox"
              class="h-4 w-4 accent-sky-500"
            >
          </label>

          <label class="inline-flex items-center gap-1.5 px-1 text-xs text-white/70 cursor-pointer">
            <span>Incidents AI</span>
            <input
              v-model="aiConfig.incidentsAiAnalysis"
              type="checkbox"
              class="h-4 w-4 accent-sky-500"
            >
          </label>

          <select
            v-model="aiConfig.aiProvider"
            class="h-7 w-[130px] rounded border border-white/15 bg-black/30 px-2 text-xs text-white"
          >
            <option value="openai">openai</option>
            <option value="anthropic">anthropic</option>
          </select>

          <select
            v-model="aiConfig.aiModel"
            class="h-7 w-[155px] rounded border border-white/15 bg-black/30 px-2 text-xs text-white"
          >
            <option value="gpt-5.4-mini">gpt-5.4-mini</option>
            <option value="gpt-5.4">gpt-5.4</option>
            <option value="gpt-5.3-codex">gpt-5.3-codex</option>
          </select>
        </div>
      </div>
    </section>

    <section class="space-y-3">
      <div class="space-y-3">
        <!-- Zero-height probe whose inner width equals a row's status-track width
             (row p-4 + track border). Measured once to derive binCount for all
             rows, instead of a ResizeObserver per row. -->
        <div class="px-4" aria-hidden="true" style="height: 0; overflow: hidden;">
          <div ref="binProbe" class="border border-white/10"></div>
        </div>

        <div class="sticky top-[50px] z-10 flex flex-wrap items-center justify-between gap-2 bg-[#13191d] py-2">
          <div class="text-xs text-white/60">Events</div>
          <div class="flex items-center gap-3">
            <label class="inline-flex items-center gap-1.5 text-xs text-white/70 cursor-pointer">
              <span>With report only</span>
              <input
                v-model="showOnlyWithReport"
                type="checkbox"
                class="h-4 w-4 accent-emerald-500"
              >
            </label>
            <input
              v-model.trim="filters.eventName"
              type="text"
              class="h-7 w-[220px] rounded-sm  bg-black/30 px-2 text-xs text-white placeholder-white/45 outline-none focus:bg-black/80"
              placeholder="Filter events by name"
            >
          </div>
        </div>

        <div v-if="loadingEvents && !filteredEvents.length" class="space-y-3">
          <bly-event-analytics-row-skeleton v-for="n in 6" :key="`skeleton-${n}`" />
        </div>

        <div v-else-if="!filteredEvents.length" class="rounded-sm  bg-[#232b33] p-4 roundedn-sm text-white/55">
          No events in selected date range.
        </div>

        <div
          v-for="event in displayedEvents"
          :id="`event-row-${event.id}`"
          :key="event.id"
          class="space-y-2 transition-colors cursor-pointer rounded-sm p-4 roundedn-sm"
          :class="selectedEventId === event.id ? 'bg-[#232b33] hover:bg-[#232b33]/40' : 'bg-[#232b33]/70 hover:bg-[#232b33]/40'"
          @click="selectEvent(event)"
        >
          <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div class="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <span class="max-w-[70%] truncate text-sm font-semibold text-white/95 md:max-w-[56%]">{{ event.fixture || event.name || event.id }}</span>
              <span
                class="rounded-full px-2 py-1 text-[10px] font-bold uppercase leading-none tracking-[0.02em]"
                :class="eventStatusTagClass(event.status)"
              >
                {{ event.status }}
              </span>
              <span
                v-if="hasReportFor(event.id)"
                class="inline-flex items-center gap-1 rounded-full bg-black/40 px-2 py-1 text-[10px] uppercase leading-none tracking-[0.02em] text-white/60"
              >
                <i class="el-icon-document"></i>report
              </span>
              <span
                v-if="isProcessingEvent(event.id)"
                class="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.03em] text-white/75"
              >
                <i class="el-icon-loading"></i>
                {{ processingActionLabel }}
              </span>
            </div>
            <div class="flex shrink-0 flex-wrap items-center gap-1">
              <button
                v-if="hasReportFor(event.id)"
                class="h-6 px-3 text-xs font-bold tracking-tight border rounded cursor-pointer transition-colors bg-black/60 border-black! text-white! hover:opacity-80"
                :disabled="analyzingIncidents && processingEventId === event.id"
                @click.stop="analyzeAll(event)"
              >
                <i v-if="analyzingIncidents && processingEventId === event.id" class="el-icon-loading mr-1"></i>
                Analyze all
              </button>
              <button
                class="h-6 px-3 text-xs font-bold tracking-tight border rounded cursor-pointer transition-colors bg-black/60 border-black! text-white! hover:opacity-80"
                :disabled="loadingReport && processingEventId === event.id && processingAction === 'report'"
                @click.stop="fetchReport(event)"
              >
                <i v-if="loadingReport && processingEventId === event.id && processingAction === 'report'" class="el-icon-loading mr-1"></i>
                Refresh
              </button>
              <button
                class="h-6 px-3 text-xs font-bold tracking-tight  rounded cursor-pointer transition-colors bg-yellow-600 text-black! hover:opacity-80"
                :disabled="generatingReport && processingEventId === event.id && processingAction === 'generate'"
                @click.stop="generateReport(event)"
              >
                <i v-if="generatingReport && processingEventId === event.id && processingAction === 'generate'" class="el-icon-loading mr-1"></i>
                Generate
              </button>
            </div>
          </div>

          <div class="flex items-center justify-between gap-2">
            <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-white/60">
              <span>{{ eventDateRangeText(event) }} * {{ eventCustomerName(event) }} / {{ eventAccountName(event) }}</span>
              <span
                v-if="eventInputName(event)"
                class="inline-flex max-w-[200px] items-center gap-1 text-blue-300"
                :title="eventInputName(event)"
              >
                <i class="el-icon-download shrink-0"></i>
                <span class="truncate">{{ eventInputName(event) }}</span>
              </span>
              <span
                v-if="eventOutputNames(event).length"
                class="inline-flex max-w-[260px] items-center gap-1 text-green-400"
                :title="eventOutputNames(event).join(', ')"
              >
                <i class="el-icon-upload2 shrink-0"></i>
                <span class="truncate">{{ eventOutputNames(event).join(', ') }}</span>
                <span v-if="eventOutputNames(event).length > 1" class="shrink-0 text-white/40">({{ eventOutputNames(event).length }})</span>
              </span>
            </div>
          </div>

          <bly-event-analytics-timeline-row
            :event="event"
            :incidents="eventIncidents(event.id)"
            :selected-incident-id="null"
            :has-report="hasReportFor(event.id)"
            :hide-header="true"
            :bin-count="binCount"
            @select-incident="handleIncidentSelect(event, $event)"
          />
        </div>

        <!-- Reveal sentinel + skeletons for the next batch loaded on scroll. -->
        <div v-if="hasMoreEvents" class="space-y-3">
          <div ref="loadMoreSentinel" class="h-px w-full"></div>
          <bly-event-analytics-row-skeleton v-for="n in 2" :key="`more-skeleton-${n}`" />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import {
  ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount, getCurrentInstance,
} from 'vue';
import moment from 'moment';
import debounce from 'lodash/debounce';
// Imports keep the `Bly`-prefixed names so the kebab-case tags in the template
// (`<bly-event-analytics-timeline-row>` etc.) resolve under script-setup's
// automatic component registration.
import BlyEventAnalyticsTimelineRow from '../components/EventAnalyticsTimelineRow.vue';
import BlyEventAnalyticsRowSkeleton from '../components/EventAnalyticsRowSkeleton.vue';

const { proxy } = getCurrentInstance();
const store = proxy.$store;

const FILTERS_STORAGE_KEY = 'eventAnalytics.filters';
// Progressive rendering: render a small first slice, then reveal more as the
// user scrolls (see loadMoreSentinel / IntersectionObserver). Reports are
// prefetched only for events that are actually rendered.
const INITIAL_RENDER = 10;
const RENDER_BATCH = 10;
// How many candidate events to request report meta for per batch when scanning
// for events that have a report (drives the "With report only" filter).
const META_BATCH = 60;

// Status-track chunk sizing — measured once here (binProbe) and passed to every
// row. A chunk targets ~6px so chunks keep a similar on-screen width across
// viewports, clamped to a sane count range.
const CHUNK_TARGET_PX = 6;
const CHUNK_MIN = 40;
const CHUNK_MAX = 240;

const defaultAiConfig = () => ({
  aiSummary: false,
  incidentsAiAnalysis: false,
  aiProvider: 'openai',
  aiModel: 'gpt-5.4-mini',
  aiMaxTokens: 1000,
});

// Reads persisted filter/AI config from localStorage, falling back to a 7-day
// window. Defined first so the reactive state below can initialise from it.
function readStoredFilters() {
  const fallback = {
    filters: {
      startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
      endDate: new Date(),
      eventName: '',
    },
    showAllEvents: false,
    showOnlyWithReport: false,
    aiConfig: defaultAiConfig(),
  };
  try {
    const raw = window.localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      filters: {
        startDate: parsed.startDate ? new Date(parsed.startDate) : fallback.filters.startDate,
        endDate: parsed.endDate ? new Date(parsed.endDate) : fallback.filters.endDate,
        eventName: parsed.eventName || '',
      },
      showAllEvents: !!parsed.showAllEvents,
      showOnlyWithReport: !!parsed.showOnlyWithReport,
      aiConfig: { ...defaultAiConfig(), ...(parsed.aiConfig || {}) },
    };
  } catch (error) {
    return fallback;
  }
}

const restored = readStoredFilters();

// Reactive state (was data()).
const filters = reactive(restored.filters);
const selectedEventId = ref(proxy.$route.query.eventId || null);
const showAllEvents = ref(restored.showAllEvents);
const showOnlyWithReport = ref(restored.showOnlyWithReport);
const aiConfig = reactive(restored.aiConfig);
const processingEventId = ref(null);
const processingAction = ref(null);
const visibleCount = ref(INITIAL_RENDER);
const binCount = ref(CHUNK_MIN);

// Template refs.
const binProbe = ref(null);
const loadMoreSentinel = ref(null);

// Non-reactive instance bookkeeping (was created()/instance fields).
// Tracks which events already had their report meta prefetched, so revealing
// more rows doesn't re-request reports for events already covered.
let prefetchedIds = new Set();
// Cursor into eventsBeforeReportFilter for the meta scan (see
// prefetchMetaUntilFilled).
let metaCursor = 0;
let revealRaf = null;
let binObserver = null;
let onScrollOrResize = null;

// Store state (was mapState).
const account = computed(() => store.state.userAccount.account);
const loadingEvents = computed(() => store.state.eventAnalytics.loadingEvents);
const loadingReport = computed(() => store.state.eventAnalytics.loadingReport);
const generatingReport = computed(() => store.state.eventAnalytics.generatingReport);
const analyzingIncidents = computed(() => store.state.eventAnalytics.analyzingIncidents);
const events = computed(() => store.state.eventAnalytics.events);
const normalizedByEventId = computed(() => store.state.eventAnalytics.normalizedByEventId);

// Store getters (each returns a function — was mapGetters).
const getEventReport = (eventId) => store.getters['eventAnalytics/getEventReport'](eventId);
const hasEventReport = (eventId) => store.getters['eventAnalytics/hasEventReport'](eventId);
const hasReportFor = (eventId) => hasEventReport(eventId);

// Events filtered by name + account, but NOT by report existence. These are
// the prefetch candidates: report meta has to be fetched from here, since the
// report filter (filteredEvents) can't apply until that meta is known.
const eventsBeforeReportFilter = computed(() => {
  const all = (events.value || []).filter(Boolean);
  const query = (filters.eventName || '').toLowerCase();
  const byName = query
    ? all.filter((event) => {
      const label = `${event.fixture || ''} ${event.name || ''} ${event.id || ''}`.toLowerCase();
      return label.includes(query);
    })
    : all;
  if (showAllEvents.value) return byName;
  const accountId = account.value && account.value.id;
  if (!accountId) return byName;
  return byName.filter((event) => event.account && event.account.id === accountId);
});
const filteredEvents = computed(() => {
  if (!showOnlyWithReport.value) return eventsBeforeReportFilter.value;
  return eventsBeforeReportFilter.value.filter((event) => hasReportFor(event.id));
});
// Only the slice currently rendered; grows as the sentinel scrolls into view.
const displayedEvents = computed(() => filteredEvents.value.slice(0, visibleCount.value));
const hasMoreEvents = computed(() => visibleCount.value < filteredEvents.value.length);
const processingActionLabel = computed(() => {
  if (processingAction.value === 'report') return 'loading report';
  if (processingAction.value === 'generate') return 'generating';
  if (processingAction.value === 'analyze') return 'analyzing';
  if (processingAction.value === 'select') return 'loading';
  return 'loading';
});

// --- Leaf helpers (no dependencies on other local functions) ---
function persistFilters() {
  try {
    const payload = {
      startDate: filters.startDate ? new Date(filters.startDate).toISOString() : null,
      endDate: filters.endDate ? new Date(filters.endDate).toISOString() : null,
      eventName: filters.eventName || '',
      showAllEvents: showAllEvents.value,
      showOnlyWithReport: showOnlyWithReport.value,
      aiConfig,
    };
    window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // ignore storage failures
  }
}
function rangeIso() {
  const start = moment.utc(filters.startDate).startOf('day').toISOString();
  const end = moment.utc(filters.endDate).endOf('day').toISOString();
  return { start, end };
}
// Open the dedicated detail view for an event (optionally with an incident
// pre-selected when an issue block is clicked).
function openDetail(eventId, incidentId) {
  const route = { name: 'eventanalyticsdetail', params: { eventId } };
  if (incidentId) route.query = { incidentId };
  proxy.$router.push(route).catch(() => {});
}
function eventIncidents(eventId) {
  const normalized = normalizedByEventId.value[eventId];
  return (normalized && normalized.incidentGroups) || [];
}
function eventCustomerName(event) {
  return event && event.customer ? event.customer.name : '-';
}
function eventAccountName(event) {
  return event && event.account ? event.account.name : '-';
}
// Event input — the override takes precedence over the base input when set.
function eventInputName(event) {
  const input = (event && (event.inputOverride || event.input)) || null;
  return input ? (input.name || input.id || '') : '';
}
// Distinct output names attached to the event (display name preferred).
function eventOutputNames(event) {
  const items = (event && event.outputs && event.outputs.items) || [];
  const names = items
    .map((item) => item && item.output && (item.output.displayName || item.output.name || item.output.id))
    .filter(Boolean);
  return Array.from(new Set(names));
}
function eventDateRangeText(event) {
  const lineup = event && event.lineupTime ? moment.utc(event.lineupTime).format('DD/MM HH:mm') : '--';
  const end = event && event.endTime ? moment.utc(event.endTime).format('DD/MM HH:mm') : '--';
  return `${lineup} -> ${end}`;
}
function eventStatusTagClass(status) {
  const normalized = `${status || ''}`.toUpperCase();
  if (normalized.includes('FAIL') || normalized.includes('ERROR') || normalized.includes('DOWN')) return 'bg-red-500/20 text-red-200';
  if (normalized.includes('LIVE') || normalized.includes('COMPLETE')) return 'bg-emerald-500/20 text-emerald-200';
  if (normalized.includes('PENDING') || normalized.includes('CONFIRMED') || normalized.includes('LINEUP') || normalized.includes('RECEIVED')) return 'bg-amber-500/20 text-amber-200';
  return 'bg-white/15 text-white/75';
}
function isProcessingEvent(eventId) {
  return processingEventId.value === eventId;
}
function setProcessing(eventId, action) {
  processingEventId.value = eventId;
  processingAction.value = action;
}
function clearProcessing(eventId) {
  if (processingEventId.value !== eventId) return;
  processingEventId.value = null;
  processingAction.value = null;
}
// Derive the shared status-track chunk count from the probe width. Every row
// has the same width, so one measurement drives them all.
function measureBins() {
  const el = binProbe.value;
  if (!el) return;
  const raw = Math.round(el.clientWidth / CHUNK_TARGET_PX);
  binCount.value = Math.max(CHUNK_MIN, Math.min(CHUNK_MAX, raw || CHUNK_MIN));
}

// --- Report prefetching ---
// Phase 1: lightweight batch — which events have a report (+ status/id).
async function prefetchReports(batchEvents) {
  const eventIds = (batchEvents || []).map((event) => event && event.id).filter(Boolean);
  if (eventIds.length === 0) return;
  await store.dispatch('eventAnalytics/fetchEventAnalyticsReportsBatch', { eventIds });
  batchEvents.forEach((event) => {
    if (event) store.dispatch('eventAnalytics/normalizeEventAnalytics', { event });
  });
}
// Phase 2: fetch full report data per event (background, on demand).
async function loadFullReports(reportEvents) {
  for (let i = 0; i < (reportEvents || []).length; i += 1) {
    const event = reportEvents[i];
    if (!event || !hasReportFor(event.id)) continue;
    const report = getEventReport(event.id);
    if (report && report.data) continue;
    // eslint-disable-next-line no-await-in-loop
    await store.dispatch('eventAnalytics/fetchEventAnalyticsReport', { eventId: event.id, reportId: report && report.reportId });
    store.dispatch('eventAnalytics/normalizeEventAnalytics', { event });
  }
}
// Walk the candidate list (ignoring the report filter) in batches, fetching
// report meta until the rendered slice is full or candidates run out.
async function prefetchMetaUntilFilled() {
  const candidates = eventsBeforeReportFilter.value;
  let guard = 0;
  while (
    metaCursor < candidates.length
    && displayedEvents.value.length < visibleCount.value
    && guard < 200
  ) {
    const batch = candidates
      .slice(metaCursor, metaCursor + META_BATCH)
      .filter((event) => event && event.id && !prefetchedIds.has(event.id));
    metaCursor = Math.min(metaCursor + META_BATCH, candidates.length);
    if (batch.length) {
      batch.forEach((event) => prefetchedIds.add(event.id));
      // eslint-disable-next-line no-await-in-loop
      await prefetchReports(batch);
    }
    guard += 1;
  }
}
// Prefetch report meta + full payloads for the rows currently rendered.
async function ensureReportsForVisible() {
  // "With report only" filters on report existence, so the visible list is
  // empty until meta is known. Scan candidates (report-agnostic) first so the
  // filter has something to match — otherwise nothing would ever load.
  if (showOnlyWithReport.value) await prefetchMetaUntilFilled();

  const pending = displayedEvents.value.filter((event) => event && event.id && !prefetchedIds.has(event.id));
  if (pending.length) {
    pending.forEach((event) => prefetchedIds.add(event.id));
    await prefetchReports(pending);
  }
  // Pull full report payloads in the background so timelines fill in.
  loadFullReports(displayedEvents.value.filter(Boolean));
}

// --- Progressive reveal / paging ---
function revealMore() {
  if (!hasMoreEvents.value) return;
  visibleCount.value = Math.min(visibleCount.value + RENDER_BATCH, filteredEvents.value.length);
  nextTick(() => {
    ensureReportsForVisible();
    // Keep filling while the sentinel stays in range (e.g. tall viewport).
    // eslint-disable-next-line no-use-before-define
    checkReveal();
  });
}
// Reveal the next batch when the sentinel is within ~600px of the viewport
// bottom. Works regardless of which element scrolls.
function checkReveal() {
  if (!hasMoreEvents.value) return;
  const sentinel = loadMoreSentinel.value;
  if (!sentinel) return;
  const viewportH = window.innerHeight || document.documentElement.clientHeight;
  if (sentinel.getBoundingClientRect().top <= viewportH + 600) revealMore();
}
// Throttle scroll/resize bursts to one geometry check per animation frame.
function scheduleRevealCheck() {
  if (revealRaf) return;
  revealRaf = requestAnimationFrame(() => {
    revealRaf = null;
    checkReveal();
  });
}
// Reset the rendered slice back to the first page, then re-check in case the
// first page doesn't fill the viewport (keeps revealing until it does).
function resetPaging() {
  visibleCount.value = INITIAL_RENDER;
  metaCursor = 0;
  nextTick(() => checkReveal());
}
// A filter changed the candidate/result set: reset paging and re-run the
// report prefetch (the "With report only" filter depends on report meta, so
// toggling it on must trigger a fresh meta scan).
function onFilterChanged() {
  resetPaging();
  nextTick(() => ensureReportsForVisible());
}
async function loadEvents() {
  const { start, end } = rangeIso();
  const result = await store.dispatch('eventAnalytics/fetchEventAnalyticsEvents', { start, end });
  if (result && result.error) {
    proxy.$message.error('Can not load events for analytics.');
    return;
  }

  // Fresh result set: forget what we prefetched and render from the top.
  prefetchedIds = new Set();
  resetPaging();
  // Only the first rendered slice gets reports up front; the rest stream in
  // as rows are revealed on scroll (see ensureReportsForVisible).
  await ensureReportsForVisible();
}
function syncFromRoute() {
  const routeEventId = proxy.$route.query.eventId;
  if (!routeEventId) return;
  selectedEventId.value = routeEventId;
  // Make sure the target row is within the rendered slice before scrolling
  // to it (it may sit past the initial page).
  const index = filteredEvents.value.findIndex((event) => event && event.id === routeEventId);
  if (index >= visibleCount.value) {
    visibleCount.value = Math.min(index + 1, filteredEvents.value.length);
    ensureReportsForVisible();
  }
  nextTick(() => {
    const el = document.getElementById(`event-row-${routeEventId}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' });
    checkReveal();
  });
}

// --- User actions ---
function selectEvent(event) {
  selectedEventId.value = event.id;
  openDetail(event.id);
}
function handleIncidentSelect(event, group) {
  openDetail(event.id, group && group.id);
}
async function fetchReport(event) {
  selectedEventId.value = event.id;
  setProcessing(event.id, 'report');
  try {
    const result = await store.dispatch('eventAnalytics/fetchEventAnalyticsReport', { eventId: event.id });
    if (result && result.error) {
      proxy.$message.error('Can not fetch analytics report.');
      return;
    }
    store.dispatch('eventAnalytics/normalizeEventAnalytics', { event });
    proxy.$message.success('Analytics report loaded');
  } finally {
    clearProcessing(event.id);
  }
}
async function generateReport(event) {
  selectedEventId.value = event.id;
  setProcessing(event.id, 'generate');
  try {
    const result = await store.dispatch('eventAnalytics/generateEventAnalyticsReport', {
      eventId: event.id,
      config: {
        aiSummary: aiConfig.aiSummary,
        incidentsAiAnalysis: aiConfig.incidentsAiAnalysis,
        aiProvider: aiConfig.aiProvider,
        aiModel: aiConfig.aiModel,
      },
    });
    if ((result && result.error) || !result || !result.success) {
      proxy.$message.error((result && result.message) || 'Report generation failed.');
      return;
    }
    proxy.$message.success(result.message || 'Report generated');
    await fetchReport(event);
  } finally {
    clearProcessing(event.id);
  }
}
async function runAnalysis(event, incidentIds) {
  const report = getEventReport(event.id);
  const reportId = report && (report.reportId || report.id);
  if (!reportId) {
    proxy.$message.error('No report to analyze. Generate the report first.');
    return;
  }
  selectedEventId.value = event.id;
  setProcessing(event.id, 'analyze');
  try {
    const result = await store.dispatch('eventAnalytics/analyzeEventAnalyticsIncidents', {
      reportId,
      incidentIds,
      config: {
        aiProvider: aiConfig.aiProvider,
        aiModel: aiConfig.aiModel,
        aiMaxTokens: aiConfig.aiMaxTokens,
        generateSummary: true,
      },
    });
    if ((result && result.error) || !result || !result.success) {
      proxy.$message.error((result && result.message) || 'Incident analysis failed.');
      return;
    }
    proxy.$message.success(result.message || 'Incident analysis complete');
    await store.dispatch('eventAnalytics/fetchEventAnalyticsReport', { eventId: event.id });
    store.dispatch('eventAnalytics/normalizeEventAnalytics', { event });
  } finally {
    clearProcessing(event.id);
  }
}
async function analyzeAll(event) {
  await runAnalysis(event, 'all');
}

// Debounced backers for the watcher-driven reload/persist (was created()).
const loadEventsDebounced = debounce(loadEvents, 400);
const persistFiltersDebounced = debounce(persistFilters, 500);

// Watcher entry points — defer to the debounced instances above.
function queueLoadEvents() {
  loadEventsDebounced();
}
function queuePersist() {
  persistFiltersDebounced();
}

// Watchers (was the watch block).
watch(() => filters.startDate, queueLoadEvents);
watch(() => filters.endDate, queueLoadEvents);
watch(filters, queuePersist, { deep: true });
watch(aiConfig, queuePersist, { deep: true });
// Persist + reset the rendered slice (and re-scan report meta) whenever a
// filter narrows/widens the result set.
watch(showAllEvents, () => { queuePersist(); onFilterChanged(); });
watch(showOnlyWithReport, () => { queuePersist(); onFilterChanged(); });
watch(() => filters.eventName, onFilterChanged);
watch(() => proxy.$route.query.eventId, syncFromRoute);

onMounted(async () => {
  // Reveal more rows as the sentinel approaches the viewport. Capture-phase so
  // a scroll on any inner container is caught too (scroll doesn't bubble).
  onScrollOrResize = scheduleRevealCheck;
  window.addEventListener('scroll', onScrollOrResize, { passive: true, capture: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });
  // Measure the chunk count once for all rows (see binProbe).
  measureBins();
  if (typeof ResizeObserver !== 'undefined' && binProbe.value) {
    binObserver = new ResizeObserver(() => measureBins());
    binObserver.observe(binProbe.value);
  } else {
    window.addEventListener('resize', measureBins);
  }
  await loadEvents();
  syncFromRoute();
});

onBeforeUnmount(() => {
  if (loadEventsDebounced && loadEventsDebounced.cancel) loadEventsDebounced.cancel();
  if (persistFiltersDebounced && persistFiltersDebounced.cancel) persistFiltersDebounced.cancel();
  if (onScrollOrResize) {
    window.removeEventListener('scroll', onScrollOrResize, { capture: true });
    window.removeEventListener('resize', onScrollOrResize);
  }
  if (binObserver) {
    binObserver.disconnect();
    binObserver = null;
  } else {
    window.removeEventListener('resize', measureBins);
  }
  if (revealRaf) cancelAnimationFrame(revealRaf);
});
</script>
